import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Footer } from '../../Component/index.js'
import { RegistrationForm } from './RegistrationForm.js'
import { tracingAxios } from '../../../Repository/Sentry.js'
import { Environment } from '../../../environment.js'
import type { RegistrationPayload } from '../../../Generated/RegistrationPayload.js'
import { useDispatch } from 'react-redux'
import { addMessage } from '../../../Flux/Slice/Application/ApplicationSlice.js'
import { errorMessage, successMessage } from '../../../Model/Message/IMessage.js'
import { AxiosError } from 'axios'
import { RegistrationSuccess } from './RegistrationSuccess.js'
import { localStorageKeyRedirectAfterRegister } from '../../Layout/PageLayout/PageLoadHooks.js'
import { trackAuth } from '../../../Services/Telemetry/index.js'

export type UserAndOrgBaseProps = {
  email?: string
  firstname?: string
  lastname?: string
  password?: string
  passwordRepeat?: string
  orgName?: string
  slug?: string
  comment?: string
}

export const RegistrationPage = () => {
  const [baseProps, setBaseProps] = useState<UserAndOrgBaseProps>({})
  const [searchParams] = useSearchParams()
  const formRef = useRef<HTMLFormElement | null>(null)
  const dispatch = useDispatch()
  const [registerSuccess, setRegisterSuccess] = useState(false)

  /* handle redirects after register */
  useEffect(() => {
    const redirect = searchParams.get('redirect')
    if (redirect) {
      localStorage.setItem(localStorageKeyRedirectAfterRegister, redirect)
    }
  }, [searchParams])

  const handleSubmit = async () => {
    if (!formRef.current || !formRef.current.checkValidity()) {
      formRef.current?.reportValidity()
      return
    }

    const payload: RegistrationPayload = {
      base: {
        organizationType: 'CUSTOMER',
        email: baseProps.email!,
        firstname: baseProps.firstname!,
        lastname: baseProps.lastname!,
        password: baseProps.password!,
        orgName: baseProps.orgName!,
      },
      /* blank stays absent, so the notification only calls out a comment when there is one */
      comment: baseProps.comment?.trim() || undefined,
    }

    trackAuth('registration_submit')
    tracingAxios
      .post(`${Environment.API_URL}/api/registration`, payload, { withCredentials: true })
      .then(() => {
        trackAuth('registration_success')
        dispatch(addMessage(successMessage('Success!')))
        setRegisterSuccess(true)
      })
      .catch((error) => {
        trackAuth('registration_failed', error instanceof AxiosError && error.response?.status ? { status: error.response.status } : {})
        if (error instanceof AxiosError) {
          const responseText = error.response?.data
          const fallback = error.message || 'Unknown error'

          const errorMsg =
            typeof responseText === 'object' && Object.hasOwn(responseText, 'errorMessage')
              ? responseText.errorMessage
              : typeof responseText === 'string' && responseText.trim()
                ? responseText.trim()
                : fallback

          dispatch(addMessage(errorMessage(`Could not complete registration: ${errorMsg}`)))
        } else {
          dispatch(addMessage(errorMessage('Could not proceed with registration, unknown error')))
        }
      })
  }

  return (
    <div className="flex flex-col min-h-screen bg-body-default">
      {/* Main Content */}
      <div className="grow z-49 flex flex-col items-center px-4">
        <div
          className="bg-white z-49 shadow-lg border border-gray-300 rounded-2xl p-6 sm:p-12 max-w-2xl w-full my-8 lg:my-[10vh]"
        >
          <img src={'/logo.png'} alt={'spcfy logo'} />

          {registerSuccess ? (
            <RegistrationSuccess />
          ) : (
            <>
              <h1 className="text-2xl font-bold text-center text-primary mb-4">Register Your Organization</h1>

              <div className={'text-large italic text-center text-gray-500'}>
                If your organization is already using spcfy, please ask your organization administrator to grant you access instead of
                registering here
              </div>

              <div className="mt-3 text-center">
                <Link to="/" className="text-sm text-primary hover:underline">
                  Already have an account? Back to login
                </Link>
              </div>

              <RegistrationForm baseProps={baseProps} setBaseProps={setBaseProps} formRef={formRef} />

              <div className="flex justify-end mt-8">
                <button className="btn btn-primary" onClick={handleSubmit}>
                  Complete Registration
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="sticky bottom-0 z-0 bg-body-default">
        <Footer />
      </div>
    </div>
  )
}

export default RegistrationPage
